import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withKeys as _withKeys, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex justify-between items-center mb-5" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", {
  "data-testid": "add-user-title",
  class: "text-[20px] text-Text/text_high font-gordita-medium"
}, " Add New User ", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("i", { class: "uil uil-times text-[24px] !mr-0" }, null, -1)
const _hoisted_4 = { class: "flex justify-center items-center w-full" }
const _hoisted_5 = { class: "add-new-user--user-info w-full" }
const _hoisted_6 = { class: "add-new-user-inputs-row" }
const _hoisted_7 = { class: "add-new-user-inputs-row" }
const _hoisted_8 = { class: "add-new-user-inputs-row" }
const _hoisted_9 = { class: "footer flex justify-end mt-10" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("hr", { class: "w-full mb-3" }, null, -1)
const _hoisted_11 = /*#__PURE__*/_createTextVNode(" Add ")

import IbxScopedCss from '@/components/IbxScopedCss/IbxScopedCss.vue';
import { IncrediField, IncrediSelect, IncrediForm, IncrediButton, useIncrediDialog, useIncrediForm } from 'incredi-ui';
import apolloClient from '@/plugins/GraphQL/Apollo/client';
import { User_Role, User_Status, useUser_CreateMutation, useUsersQuery } from '@/graphql/codegen/generated/graphql';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useNotify } from '@/services/notify/notify';
import { useI18n } from 'vue-i18n';
import { UsersDocument } from '@/graphql/codegen/gql/graphql';
import { validatePassword, validateEmail, validateUserName, validateAllWhiteSpaces } from '@/utils/string-validations';
import { useMatomo } from '@/services/matomo/matomo.service';
import { MatomoCategories, MatomoUserActions } from '@/services/matomo/matomo.const';
import elementIds from '@/const/elementIds.json';
import { MAP_User_Roles_To_Show } from './users-table.const';


export default /*#__PURE__*/_defineComponent({
  __name: 'addNewUserDialog',
  setup(__props) {

const matomo = useMatomo();
const users = useUsersQuery({}, { fetchPolicy: 'cache-first' });
const { toastMessage } = useNotify();
const { t } = useI18n();
const dialog = useIncrediDialog();
const createUser = useUser_CreateMutation();
const roles = Object.values(User_Role).map(role => ({
  value: MAP_User_Roles_To_Show[role],
  key: role,
}));
const { form, fields } = useIncrediForm({
  initialValues: {
    userName: '',
    password: '',
    role: {
      key: MAP_User_Roles_To_Show[User_Role.Viewer],
      value: User_Role.Viewer,
    },
    status: User_Status.Active,
    email: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
  },
  validationSchema: {
    password: [
      (v: string) => {
        if (!form.values.password) {
          return t('users.userFormRules.required');
        }
        return validatePassword(v) || t('users.userFormRules.password');
      },
    ],
    confirmPassword: [
      (v: string) => {
        if (!form.values.confirmPassword) {
          return t('users.userFormRules.required');
        }
        return v === form.values.password || t('users.userFormRules.confirmPassword');
      },
    ],
    email: [(v: any) => !v || validateEmail(v as any) || t('users.userFormRules.email')],
    userName: [
      (v: string) => {
        if (!form.values.userName) {
          return t('users.userFormRules.required');
        }
        return validateUserName(v) || t('users.userFormRules.userName');
      },
    ],
    firstName: [
      (v: string) => {
        if (validateAllWhiteSpaces(v)) {
          return t('users.userFormRules.invalid');
        }
        return true;
      },
    ],
    lastName: [
      (v: string) => {
        if (validateAllWhiteSpaces(v)) {
          return t('users.userFormRules.invalid');
        }
        return true;
      },
    ],
  },
});

const closeDialog = () => {
  dialog?.close('addNewUserDialog');
};

const handleSubmit = form.handleSubmit(async () => {
  const variables = {
    data: {
      userName: form.values.userName,
      password: form.values.password,
      role: form.values.role.key as User_Role,
      status: form.values.status,
      additionalInfo: {
        firstName: form.values.firstName,
        lastName: form.values.lastName,
        email: form.values.email.length > 0 ? form.values.email : null, // for backend validation
      },
    },
  };
  try {
    const result = await createUser.mutate(variables);
    if (result?.data?.user_create) {
      matomo?.trackEvent({
        category: MatomoCategories.User,
        action: MatomoUserActions.Add,
        name: result?.data?.user_create?.role,
      });
      apolloClient.writeQuery({
        query: UsersDocument,
        data: {
          users: [...(users.result.value?.users || []), result?.data?.user_create],
        },
      });
      closeDialog();
      toastMessage(t('users.userAdded.success'), CONST_NOTIFY.MESSAGE_TYPE.SUCCESS);
    }
  } catch (e: any) {
    const value = (e as Error).message;
    if (value.includes('already exists')) {
      form.setErrors({ userName: t('users.notUniqueUserName') });
      return;
    }
    closeDialog();
    toastMessage(t('users.userAdded.fail'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(IbxScopedCss, null, {
    default: _withCtx(() => [
      _createVNode(_unref(IncrediForm), {
        class: "add-new-user--dialog-container",
        onSubmit: _withModifiers(_unref(handleSubmit), ["prevent"]),
        onKeydown: _cache[12] || (_cache[12] = _withKeys(_withModifiers(() => {}, ["prevent"]), ["enter"]))
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _hoisted_2,
            _createVNode(_unref(IncrediButton), {
              "data-testid": _unref(elementIds).users.create.buttons.closeDialogButton,
              size: "small",
              design: "ghost-grey",
              fab: "",
              class: "w-[32px] -mr-2",
              onClick: closeDialog
            }, {
              default: _withCtx(() => [
                _hoisted_3
              ]),
              _: 1
            }, 8, ["data-testid"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(IncrediField), {
                  modelValue: _unref(fields).userName.value.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(fields).userName.value.value) = $event)),
                  "data-testid": _unref(elementIds).users.create.inputs.username,
                  error: !!_unref(form).errors.value.userName,
                  "error-message": _unref(form).errors.value.userName,
                  maxlength: 20,
                  label: "Username",
                  size: "big"
                }, null, 8, ["modelValue", "data-testid", "error", "error-message"]),
                _createVNode(_unref(IncrediSelect), {
                  modelValue: _unref(fields).role.value.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(fields).role.value.value) = $event)),
                  "data-testid": _unref(elementIds).users.create.inputs.role,
                  class: "new-user-role-select",
                  size: "big",
                  label: "Role",
                  items: _unref(roles),
                  "override-style": { width: '264px' },
                  "key-field": "key",
                  "value-field": "value"
                }, null, 8, ["modelValue", "data-testid", "items"])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_unref(IncrediField), {
                  modelValue: _unref(fields).password.value.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(fields).password.value.value) = $event)),
                  "data-testid": _unref(elementIds).users.create.inputs.password,
                  error: !!_unref(form).errors.value.password,
                  "error-message": _unref(form).errors.value.password,
                  label: "Password",
                  "field-type": "password",
                  size: "big",
                  onChange: _cache[3] || (_cache[3] = () => _unref(fields).password.validate())
                }, null, 8, ["modelValue", "data-testid", "error", "error-message"]),
                _createVNode(_unref(IncrediField), {
                  modelValue: _unref(fields).confirmPassword.value.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(fields).confirmPassword.value.value) = $event)),
                  "data-testid": _unref(elementIds).users.create.inputs.confirmPassword,
                  error: !!_unref(form).errors.value.confirmPassword,
                  "error-message": _unref(form).errors.value.confirmPassword,
                  label: "Repeat Password",
                  "field-type": "password",
                  size: "big",
                  onChange: _cache[5] || (_cache[5] = () => _unref(fields).confirmPassword.validate())
                }, null, 8, ["modelValue", "data-testid", "error", "error-message"])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(_unref(IncrediField), {
                  modelValue: _unref(fields).firstName.value.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(fields).firstName.value.value) = $event)),
                  "data-testid": _unref(elementIds).users.create.inputs.firstName,
                  error: !!_unref(form).errors.value.firstName,
                  "error-message": _unref(form).errors.value.firstName,
                  label: "First Name (Optional)",
                  size: "big",
                  maxlength: 30,
                  onChange: _cache[7] || (_cache[7] = () => _unref(fields).firstName.validate())
                }, null, 8, ["modelValue", "data-testid", "error", "error-message"]),
                _createVNode(_unref(IncrediField), {
                  modelValue: _unref(fields).lastName.value.value,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(fields).lastName.value.value) = $event)),
                  "data-testid": _unref(elementIds).users.create.inputs.lastName,
                  error: !!_unref(form).errors.value.lastName,
                  "error-message": _unref(form).errors.value.lastName,
                  label: "Last Name (Optional)",
                  size: "big",
                  maxlength: 30,
                  onChange: _cache[9] || (_cache[9] = () => _unref(fields).lastName.validate())
                }, null, 8, ["modelValue", "data-testid", "error", "error-message"])
              ]),
              _createVNode(_unref(IncrediField), {
                modelValue: _unref(fields).email.value.value,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_unref(fields).email.value.value) = $event)),
                "data-testid": _unref(elementIds).users.create.inputs.email,
                error: !!_unref(form).errors.value.email,
                "error-message": _unref(form).errors.value.email,
                label: "Email (Optional)",
                size: "big",
                onChange: _cache[11] || (_cache[11] = () => _unref(fields).email.validate())
              }, null, 8, ["modelValue", "data-testid", "error", "error-message"])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _hoisted_10,
            _createVNode(_unref(IncrediButton), {
              "data-testid": _unref(elementIds).users.create.buttons.save,
              class: "items-center justify-center",
              type: "submit",
              size: "small",
              color: "primary",
              disabled: !_unref(form).meta.value.dirty,
              onKeydown: _withKeys(_withModifiers(_unref(handleSubmit), ["prevent"]), ["enter"])
            }, {
              default: _withCtx(() => [
                _hoisted_11
              ]),
              _: 1
            }, 8, ["data-testid", "disabled", "onKeydown"])
          ])
        ]),
        _: 1
      }, 8, ["onSubmit"])
    ]),
    _: 1
  }))
}
}

})